import { useRef, useState, type ReactNode, type RefObject } from 'react'
import { useFrame } from '@react-three/fiber'
import type { Group } from 'three'
import { XRSpace, XRMeshModel, XRPlaneModel, useXRMeshes, useXRPlanes } from '@react-three/xr'
import { RigidBody, MeshCollider } from '@react-three/rapier'
import { useDebugEnabled } from '../../app/debug'

type PosedGroup = Group & { transformReady?: boolean }

// XRSpace sets `transformReady` on its group once the real-world pose resolves (it updates the
// matrix at frame priority -100, before any default useFrame). A fixed collider created before
// then would be placed at the origin, so gate the RigidBody on it. The room is static, so a fixed
// body created once at the settled pose stays correct — it won't follow a mid-session
// re-localization, which is acceptable for now.
function WhenPosed({
  spaceRef,
  children,
}: {
  spaceRef: RefObject<Group | null>
  children: ReactNode
}) {
  const [posed, setPosed] = useState(false)
  useFrame(() => {
    if (!posed && (spaceRef.current as PosedGroup | null)?.transformReady) setPosed(true)
  })
  return posed ? <>{children}</> : null
}

// Default: a depth-only occluder — writes depth so virtual models behind real geometry get clipped
// (passthrough shows through), writes no color. Debug: a wireframe so the scanned room is visible.
// Either way the mesh stays visible, which is what feeds the trimesh collider.
function roomMaterial(debug: boolean, color: string) {
  return debug ? (
    <meshBasicMaterial color={color} wireframe transparent opacity={0.25} />
  ) : (
    <meshBasicMaterial colorWrite={false} />
  )
}

// One static trimesh collider + occluder per detected mesh/plane, sized from the library's live
// geometry. Keyed by lastChangedTime so a refined room geometry remounts. renderOrder -1 draws the
// occluder before scene content so its depth is in place. `includeInvisible` keeps the collider
// generating even if the model's visibility ever changes (r3f skips invisible meshes by default).
function MeshBody({ mesh, debug }: { mesh: XRMesh; debug: boolean }) {
  const spaceRef = useRef<Group>(null)
  return (
    <XRSpace ref={spaceRef} space={mesh.meshSpace}>
      <WhenPosed spaceRef={spaceRef}>
        <RigidBody key={mesh.lastChangedTime} type="fixed" colliders={false} includeInvisible>
          <MeshCollider type="trimesh">
            <XRMeshModel mesh={mesh} renderOrder={-1}>
              {roomMaterial(debug, '#ff3df0')}
            </XRMeshModel>
          </MeshCollider>
        </RigidBody>
      </WhenPosed>
    </XRSpace>
  )
}

function PlaneBody({ plane, debug }: { plane: XRPlane; debug: boolean }) {
  const spaceRef = useRef<Group>(null)
  return (
    <XRSpace ref={spaceRef} space={plane.planeSpace}>
      <WhenPosed spaceRef={spaceRef}>
        <RigidBody key={plane.lastChangedTime} type="fixed" colliders={false} includeInvisible>
          <MeshCollider type="trimesh">
            <XRPlaneModel plane={plane} renderOrder={-1}>
              {roomMaterial(debug, '#3df0ff')}
            </XRPlaneModel>
          </MeshCollider>
        </RigidBody>
      </WhenPosed>
    </XRSpace>
  )
}

// Real-room geometry (Quest scene meshes + planes) as fixed Rapier colliders, so physics-enabled
// models land on the table/floor and collide with walls. Empty outside AR (the hooks return []).
export function RealWorldColliders() {
  const meshes = useXRMeshes()
  const planes = useXRPlanes()
  const debug = useDebugEnabled()
  return (
    <>
      {meshes.map((mesh, i) => (
        <MeshBody key={i} mesh={mesh} debug={debug} />
      ))}
      {planes.map((plane, i) => (
        <PlaneBody key={i} plane={plane} debug={debug} />
      ))}
    </>
  )
}
